import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import formatter from 'services/formatter'
import { getInventoryLogsRequest } from 'store/modules/admin/logs/reducer'

// ---------------- utils ---------------- //

const aggregateByField = (logs, field) =>
  logs.reduce((acc, log) => {
    const key = log[field]
    if (key) acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})

// ------------------------------------ //

export const useInventoryAPILogs = (inventoryLogs) => {
  const dispatch = useDispatch()
  const [periodFilter, setPeriodFilter] = useState('lastWeek')
  const [searchApiType, setSearchApiType] = useState('by_product')

  useEffect(() => {
    dispatch(
      getInventoryLogsRequest({
        created_at: '',
        web_company_id: '',
        product_id: '',
        search_api_type: '',
      })
    )
  }, [dispatch])

  const filteredLogs = useMemo(() => {
    const today = new Date()
    const dateLimits = {
      lastWeek: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7
      ),
      lastMonth: new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        today.getDate()
      ),
      lastYear: new Date(
        today.getFullYear() - 1,
        today.getMonth(),
        today.getDate()
      ),
    }

    return inventoryLogs.filter((log) => {
      const logDate = new Date(log.created_at)
      const isInPeriod = logDate >= dateLimits[periodFilter]

      const isTypeMatch =
        searchApiType === 'complete' ? log.product_id == null : log.product_id

      return isInPeriod && isTypeMatch
    })
  }, [inventoryLogs, periodFilter, searchApiType])

  // ------ Rankings and Counts ------ //
  const userCount = useMemo(
    () => aggregateByField(filteredLogs, 'web_company_descr'),
    [filteredLogs]
  )
  const productCount = useMemo(
    () => aggregateByField(filteredLogs, 'product_id'),
    [filteredLogs]
  )

  const topUser = useMemo(
    () =>
      Object.entries(userCount).reduce(
        (max, [user, count]) => (count > max.count ? { user, count } : max),
        { user: null, count: 0 }
      ),
    [userCount]
  )

  const topProduct = useMemo(
    () =>
      Object.entries(productCount).reduce(
        (max, [productId, count]) =>
          count > max.count ? { productId, count } : max,
        { productId: null, count: 0 }
      ),
    [productCount]
  )

  // ------ Chart Data ------ //
  const graphData = useMemo(() => {
    const ascLogs = [...filteredLogs].reverse()

    const grouped =
      periodFilter === 'lastYear'
        ? ascLogs.reduce((acc, log) => {
            const date = new Date(log.created_at)
            const monthLabel = `${date.toLocaleString('pt-BR', {
              month: 'short',
            })}/${date.getFullYear().toString().slice(-2)}`
            acc[monthLabel] = (acc[monthLabel] || 0) + 1
            return acc
          }, {})
        : ascLogs.reduce((acc, log) => {
            const date = formatter(log.created_at).toSimpleDate().slice(0, 5)
            acc[date] = (acc[date] || 0) + 1
            return acc
          }, {})

    return {
      labels: Object.keys(grouped),
      series: [Object.values(grouped)],
    }
  }, [filteredLogs, periodFilter])

  const totalLogs = filteredLogs.length

  return {
    periodFilter,
    setPeriodFilter,
    setSearchApiType,
    searchApiType,
    filteredLogs,
    totalLogs,
    topUser,
    topProduct,
    userCount,
    productCount,
    graphData,
  }
}
